import type { FC, ReactElement, ReactNode } from 'react';
import { useEffect } from 'react';

import { useGlobalEvent } from '@luna/events';

type Props = {
    setIsNavigationVisible: (isVisible: boolean) => void;
    children?: ReactNode;
};

/**
 * HideNavigation is a component that manages the visibility of navigation elements.
 * When rendered, it hides the navigation and restores it when unmounted.
 *
 * @param {Object} props
 * @param {function} props.setIsNavigationVisible - Function to set the visibility state of the navigation
 * @param {ReactNode} [props.children] - Child elements to render within this component
 *
 * @returns {ReactElement} The children passed to this component
 *
 * @example
 * ```tsx
 * import { HideNavigation } from '@luna/router';
 *
 * const FullScreenPage = () => {
 *   const [isNavVisible, setIsNavVisible] = useState(true);
 *
 *   return (
 *     <HideNavigation setIsNavigationVisible={setIsNavVisible}>
 *       <div className="full-screen-content">
 *        <Content/>
 *       </div>
 *     </HideNavigation>
 *   );
 * };
 *
 * @remarks
 * This component is useful for:
 * - Focus pages.
 * - Temporarily removing navigation for specific routes or components.
 */
export const HideNavigation: FC<Props> = ({ setIsNavigationVisible, children }) => {
    const trigger = useGlobalEvent('ui:navigation');

    useEffect(() => {
        setIsNavigationVisible(false);
        trigger(false);

        return () => {
            setIsNavigationVisible(true);
            trigger(true);
        };
    }, []);

    return children as ReactElement;
};
